import type { Database } from './sqlite';

/** Найденное. Подпись собирает репозиторий: экран не должен ходить в базу за именами. */
export interface SearchHit {
  id: string;
  title: string;
  subtitle: string | null;
  /** Дело найденного, если оно есть: по нему открывают задачу. */
  caseId?: string | null;
  /** Срок задачи; форматирует экран — там известны локаль и часовой пояс. */
  at?: number | null;
  /** Избранный документ: в выдаче стоит выше прочих и помечен звездой — [D-20]. */
  isFavorite?: boolean;
}

export interface SearchResults {
  cases: SearchHit[];
  tasks: SearchHit[];
  documents: SearchHit[];
  clients: SearchHit[];
}

const EMPTY: SearchResults = { cases: [], tasks: [], documents: [], clients: [] };

/** Те же числа, что на Android: с одной буквы ищется всё подряд, а выдача не бесконечна. */
const MIN_QUERY_LENGTH = 2;
const LIMIT = 20;
const RECENT_LIMIT = 5;

/**
 * Глобальный поиск.
 *
 * **`LIKE`, а не полнотекстовый индекс** — то же решение, что на Android
 * ([DATA_MODEL.md](docs/DATA_MODEL.md)): отдельная таблица FTS требует синхронизации
 * с основными и способна незаметно разойтись с данными, а для поиска это худший вид
 * дефекта. На объёмах персональной практики полный проход занимает единицы миллисекунд.
 * FTS появится вместе с OCR, когда искать понадобится по содержимому документов.
 *
 * Ищем по клиентам, делам, названиям документов, заметкам, **задачам** ([D-15])
 * и именам тегов ([D-19]).
 *
 * **Известное ограничение, общее с Android:** `LIKE` в SQLite приводит регистр только
 * у латиницы, поэтому «иванов» не найдёт «Иванов». Ведём себя так же, как телефон —
 * расхождение между клиентами было бы хуже самого ограничения. Записано в бэклог
 * как задача для обоих клиентов сразу.
 */
export function searchRepository(db: Database) {
  const like = (query: string) => `%${query.trim()}%`;

  return {
    async search(query: string): Promise<SearchResults> {
      if (query.trim().length < MIN_QUERY_LENGTH) return EMPTY;
      const pattern = like(query);

      const cases = await db.query(
        `SELECT c.id AS id, c.title AS title, cl.fullName AS client
           FROM cases c LEFT JOIN clients cl ON cl.id = c.clientId
          WHERE c.deletedAt IS NULL
            AND (c.title LIKE ? OR c.notes LIKE ? OR c.description LIKE ?
                 OR c.id IN (SELECT ct.caseId FROM case_tags ct
                              JOIN tags t ON t.id = ct.tagId WHERE t.name LIKE ?))
          ORDER BY c.updatedAt DESC LIMIT ?`,
        [pattern, pattern, pattern, pattern, LIMIT],
      );

      const tasks = await db.query(
        `SELECT e.id AS id, e.title AS title, e.caseId AS caseId,
                COALESCE(e.deadlineAt, e.startAt) AS at, c.title AS caseTitle
           FROM events e LEFT JOIN cases c ON c.id = e.caseId
          WHERE e.deletedAt IS NULL AND (e.title LIKE ? OR e.description LIKE ?)
          ORDER BY at LIMIT ?`,
        [pattern, pattern, LIMIT],
      );

      const documents = await db.query(
        `SELECT d.id AS id, d.title AS title, d.isFavorite AS isFavorite,
                d.caseId AS caseId, c.title AS caseTitle
           FROM documents d LEFT JOIN cases c ON c.id = d.caseId
          WHERE d.deletedAt IS NULL AND d.title LIKE ?
          ORDER BY d.isFavorite DESC, d.addedAt DESC LIMIT ?`,
        [pattern, LIMIT],
      );

      const clients = await db.query(
        `SELECT id, fullName, phone FROM clients
          WHERE deletedAt IS NULL
            AND (fullName LIKE ? OR phone LIKE ? OR email LIKE ? OR notes LIKE ?)
          ORDER BY fullName COLLATE NOCASE LIMIT ?`,
        [pattern, pattern, pattern, pattern, LIMIT],
      );

      return {
        cases: cases.map((row) => ({
          id: String(row.id),
          title: String(row.title),
          subtitle: row.client === null ? null : String(row.client),
        })),
        tasks: tasks.map((row) => ({
          id: String(row.id),
          title: String(row.title),
          subtitle: row.caseTitle === null ? null : String(row.caseTitle),
          caseId: row.caseId === null ? null : String(row.caseId),
          at: row.at === null ? null : Number(row.at),
        })),
        documents: documents.map((row) => ({
          id: String(row.id),
          title: String(row.title),
          subtitle: row.caseTitle === null ? null : String(row.caseTitle),
          caseId: row.caseId === null ? null : String(row.caseId),
          isFavorite: row.isFavorite === 1,
        })),
        clients: clients.map((row) => ({
          id: String(row.id),
          title: String(row.fullName),
          subtitle: row.phone === null ? null : String(row.phone),
        })),
      };
    },

    /**
     * Что показать до первого символа запроса.
     *
     * Пустой экран поиска — потерянное касание: чаще всего юрист возвращается к тому,
     * с чем работал сегодня ([D-11]).
     */
    async recent(limit = RECENT_LIMIT): Promise<SearchResults> {
      const cases = await db.query(
        `SELECT c.id AS id, c.title AS title, cl.fullName AS client
           FROM cases c LEFT JOIN clients cl ON cl.id = c.clientId
          WHERE c.deletedAt IS NULL ORDER BY c.updatedAt DESC LIMIT ?`,
        [limit],
      );

      const documents = await db.query(
        `SELECT d.id AS id, d.title AS title, d.isFavorite AS isFavorite, c.title AS caseTitle
           FROM documents d LEFT JOIN cases c ON c.id = d.caseId
          WHERE d.deletedAt IS NULL ORDER BY d.isFavorite DESC, d.addedAt DESC LIMIT ?`,
        [limit],
      );

      return {
        ...EMPTY,
        cases: cases.map((row) => ({
          id: String(row.id),
          title: String(row.title),
          subtitle: row.client === null ? null : String(row.client),
        })),
        documents: documents.map((row) => ({
          id: String(row.id),
          title: String(row.title),
          subtitle: row.caseTitle === null ? null : String(row.caseTitle),
          isFavorite: row.isFavorite === 1,
        })),
      };
    },
  };
}

export type SearchRepository = ReturnType<typeof searchRepository>;
